import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Pencil, Save, Trash2, UserRound } from 'lucide-react';
import { api } from '../../services/api';

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function buildPersonKey(person) {
  const dbId = person?._dbId ?? person?.id ?? '';
  if (dbId !== '' && dbId !== null && dbId !== undefined) return `db:${dbId}`;
  const dni = String(person?.dni || '').trim();
  return dni ? `dni:${dni}` : '';
}

function emptyForm() {
  const today = todayYmd();
  return {
    id: '',
    person_key: '',
    person_db_id: '',
    person_dni: '',
    person_name: '',
    person_position: '',
    person_area: '',
    person_phone: '',
    person_email: '',
    person_project: '',
    person_status: '',
    person_snapshot: null,
    start_date: today,
    end_date: today,
    notes: '',
  };
}

function parseSnapshot(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

function rowToForm(row) {
  return {
    id: row?.id || '',
    person_key: row?.person_db_id ? `db:${row.person_db_id}` : (row?.person_dni ? `dni:${row.person_dni}` : ''),
    person_db_id: row?.person_db_id || '',
    person_dni: row?.person_dni || '',
    person_name: row?.person_name || '',
    person_position: row?.person_position || '',
    person_area: row?.person_area || '',
    person_phone: row?.person_phone || '',
    person_email: row?.person_email || '',
    person_project: row?.person_project || '',
    person_status: row?.person_status || '',
    person_snapshot: parseSnapshot(row?.person_snapshot),
    start_date: row?.start_date || todayYmd(),
    end_date: row?.end_date || todayYmd(),
    notes: row?.notes || '',
  };
}

export default function PersonalTrackingModule({
  location,
  people = [],
  reportKind = 'accidentalidad',
}) {
  const config = useMemo(() => {
    if (reportKind === 'ausentismo') {
      return {
        title: 'Ausentismos',
        actionLabel: 'Reportar ausentismo',
        emptyText: 'Aun no hay ausentismos reportados.',
        headerClass: 'border-orange-100 bg-gradient-to-br from-orange-50 via-white to-yellow-50',
        accentText: 'text-orange-800',
        badgeClass: 'bg-orange-100 text-orange-700',
        buttonClass: 'bg-orange-600 hover:bg-orange-700',
        cardClass: 'border-orange-200 bg-orange-50',
        icon: CheckCircle2,
        apiClient: api.sgi.absenteeism,
      };
    }
    return {
      title: 'Accidentalidad',
      actionLabel: 'Reportar accidentalidad',
      emptyText: 'Aun no hay eventos de accidentalidad reportados.',
      headerClass: 'border-yellow-100 bg-gradient-to-br from-yellow-50 via-white to-orange-50',
      accentText: 'text-yellow-800',
      badgeClass: 'bg-yellow-100 text-yellow-700',
      buttonClass: 'bg-yellow-600 hover:bg-yellow-700',
      cardClass: 'border-yellow-200 bg-yellow-50',
      icon: AlertTriangle,
      apiClient: api.sgi.accidentality,
    };
  }, [reportKind]);

  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  const sortedPeople = useMemo(
    () => [...(Array.isArray(people) ? people : [])].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''))),
    [people]
  );

  const selectedPerson = useMemo(() => {
    const snapshot = parseSnapshot(form.person_snapshot);
    if (snapshot) return snapshot;
    return {
      dni: form.person_dni,
      name: form.person_name,
      position: form.person_position,
      area: form.person_area,
      phone: form.person_phone,
      email: form.person_email,
      project: form.person_project,
      status: form.person_status,
    };
  }, [form]);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await config.apiClient.list({ location, limit: 1000, order: 'desc' });
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error(`Error cargando ${reportKind}`, err);
      setFeedback(`No se pudo cargar la información de ${config.title.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  }, [config.apiClient, config.title, location, reportKind]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handlePersonChange = (event) => {
    const nextKey = event.target.value;
    const person = sortedPeople.find((item) => buildPersonKey(item) === nextKey);
    if (!person) {
      setForm((prev) => ({
        ...prev,
        person_key: '',
        person_db_id: '',
        person_dni: '',
        person_name: '',
        person_position: '',
        person_area: '',
        person_phone: '',
        person_email: '',
        person_project: '',
        person_status: '',
        person_snapshot: null,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      person_key: nextKey,
      person_db_id: person._dbId ?? person.id ?? '',
      person_dni: String(person.dni || '').trim(),
      person_name: String(person.name || '').trim(),
      person_position: String(person.position || '').trim(),
      person_area: String(person.area || '').trim(),
      person_phone: String(person.phone || '').trim(),
      person_email: String(person.email || '').trim(),
      person_project: String(person.project || person.area || '').trim(),
      person_status: String(person.status || '').trim(),
      person_snapshot: person,
    }));
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.person_dni || !form.person_name || !form.start_date || !form.end_date) {
      setFeedback('Selecciona un colaborador activo y completa fecha inicio y fecha fin.');
      return;
    }
    if (form.end_date < form.start_date) {
      setFeedback('La fecha fin no puede ser menor que la fecha inicio.');
      return;
    }

    try {
      setSaving(true);
      setFeedback('');
      await config.apiClient.upsert({
        id: form.id || undefined,
        location,
        person_db_id: form.person_db_id || undefined,
        person_dni: form.person_dni,
        person_name: form.person_name,
        person_position: form.person_position,
        person_area: form.person_area,
        person_phone: form.person_phone,
        person_email: form.person_email,
        person_project: form.person_project,
        person_status: form.person_status,
        person_snapshot: form.person_snapshot || selectedPerson || null,
        start_date: form.start_date,
        end_date: form.end_date,
        notes: form.notes,
      });
      setForm(emptyForm());
      setFeedback(form.id ? 'Registro actualizado correctamente.' : 'Registro guardado correctamente.');
      await loadRows();
    } catch (err) {
      console.error(`Error guardando ${reportKind}`, err);
      setFeedback(err?.message || 'No se pudo guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setForm(rowToForm(row));
    setFeedback(`Editando registro #${row.id}.`);
  };

  const handleDelete = async (row) => {
    const ok = window.confirm(`¿Eliminar el registro #${row.id}?`);
    if (!ok) return;
    try {
      await config.apiClient.delete({ id: row.id });
      if (String(form.id) === String(row.id)) setForm(emptyForm());
      setFeedback('Registro eliminado.');
      await loadRows();
    } catch (err) {
      console.error(`Error eliminando ${reportKind}`, err);
      setFeedback(err?.message || 'No se pudo eliminar el registro.');
    }
  };

  const handleCancel = () => {
    setForm(emptyForm());
    setFeedback('');
  };

  const Icon = config.icon;

  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border p-4 shadow-sm ${config.headerClass}`}>
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${config.badgeClass}`}>
            <Icon size={20} />
          </div>
          <div>
            <div className="text-base font-semibold text-gray-800">{config.title}</div>
            <div className="text-sm text-gray-600">{config.actionLabel} y llevar trazabilidad por colaborador.</div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 text-base font-semibold text-gray-800">{config.actionLabel}</div>

        <div className="grid gap-4 md:grid-cols-3">
          <label className="block md:col-span-3">
            <span className="mb-1 block text-sm font-medium text-gray-700">Personal activo</span>
            <select
              value={form.person_key}
              onChange={handlePersonChange}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="">Selecciona un colaborador</option>
              {sortedPeople.map((person) => (
                <option key={buildPersonKey(person)} value={buildPersonKey(person)}>
                  {`${person.name || 'Sin nombre'}${person.dni ? ` - ${person.dni}` : ''}${person.position ? ` - ${person.position}` : ''}`}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Fecha inicio</span>
            <input
              type="date"
              value={form.start_date}
              onChange={handleChange('start_date')}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Fecha fin</span>
            <input
              type="date"
              value={form.end_date}
              onChange={handleChange('end_date')}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </label>

          <label className="block md:col-span-1">
            <span className="mb-1 block text-sm font-medium text-gray-700">Observaciones</span>
            <textarea
              value={form.notes}
              onChange={handleChange('notes')}
              rows={3}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              placeholder="Describe el evento o seguimiento"
            />
          </label>
        </div>

        <div className={`mt-4 rounded-2xl border p-4 ${config.cardClass}`}>
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
            <UserRound size={16} />
            Datos del colaborador
          </div>
          <div className="grid gap-3 text-sm text-gray-700 md:grid-cols-2 xl:grid-cols-4">
            <div><span className="font-semibold">Cédula:</span> {selectedPerson?.dni || form.person_dni || '-'}</div>
            <div><span className="font-semibold">Nombre:</span> {selectedPerson?.name || form.person_name || '-'}</div>
            <div><span className="font-semibold">Cargo:</span> {selectedPerson?.position || form.person_position || '-'}</div>
            <div><span className="font-semibold">Área:</span> {selectedPerson?.area || form.person_area || '-'}</div>
            <div><span className="font-semibold">Proyecto:</span> {selectedPerson?.project || form.person_project || '-'}</div>
            <div><span className="font-semibold">Teléfono:</span> {selectedPerson?.phone || form.person_phone || '-'}</div>
            <div><span className="font-semibold">Correo:</span> {selectedPerson?.email || form.person_email || '-'}</div>
            <div><span className="font-semibold">Estado:</span> {selectedPerson?.status || form.person_status || '-'}</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={saving}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white shadow-sm transition ${config.buttonClass}`}
          >
            <Save size={16} />
            {saving ? 'Guardando...' : (form.id ? 'Actualizar' : 'Guardar')}
          </button>
          {form.id && (
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Cancelar
            </button>
          )}
        </div>

        {feedback && (
          <div className={`mt-3 rounded-xl px-3 py-2 text-sm ${config.badgeClass}`}>
            {feedback}
          </div>
        )}
      </form>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3 text-sm font-semibold text-gray-800">Registros guardados</div>
        {loading ? (
          <div className="text-sm text-gray-500">Cargando registros...</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-gray-500">{config.emptyText}</div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div
                key={row.id}
                className="rounded-2xl border border-gray-200 bg-gray-50 p-4"
              >
                <div className="grid gap-3 text-sm text-gray-700 md:grid-cols-2 xl:grid-cols-4">
                  <div><span className="font-semibold">Cédula:</span> {row.person_dni || '-'}</div>
                  <div><span className="font-semibold">Nombre:</span> {row.person_name || '-'}</div>
                  <div><span className="font-semibold">Cargo:</span> {row.person_position || '-'}</div>
                  <div><span className="font-semibold">Área:</span> {row.person_area || '-'}</div>
                  <div><span className="font-semibold">Proyecto:</span> {row.person_project || '-'}</div>
                  <div><span className="font-semibold">Inicio:</span> {row.start_date || '-'}</div>
                  <div><span className="font-semibold">Fin:</span> {row.end_date || '-'}</div>
                  <div><span className="font-semibold">Estado:</span> {row.person_status || '-'}</div>
                </div>
                {row.notes && (
                  <div className="mt-3 text-sm text-gray-700">
                    <span className="font-semibold">Observaciones:</span> {row.notes}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(row)}
                    className="inline-flex items-center gap-1 rounded-xl border border-yellow-300 bg-yellow-50 px-3 py-1.5 text-xs font-medium text-yellow-700 transition hover:bg-yellow-100"
                  >
                    <Pencil size={14} />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(row)}
                    className="inline-flex items-center gap-1 rounded-xl border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 transition hover:bg-red-100"
                  >
                    <Trash2 size={14} />
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
